"use client";

import React from "react";
import { useSelector } from "react-redux";
import { createPortal } from "react-dom";
import { RootState } from "@/store/store";
import { useSnackbar } from "@/hooks/useSnackbar";
import SnackbarItem from "./SnackbarItem";

/**
 * SnackbarContainer (updated)
 *
 * - DOES NOT stack: only the newest snackbar is shown.
 * - Newest snackbar appears in the top-right.
 * - Passes a default duration (8s) to the SnackbarItem which
 *   handles pause-on-hover and auto-dismiss logic.
 *
 * If you prefer a different behavior (queue vs replace), let me know.
 */
const DEFAULT_DURATION_MS = 8000;

const SnackbarContainer: React.FC = () => {
  const selectSnackbars = (state: RootState) => state.snackbar.snackbars;
  const snackbars = useSelector(selectSnackbars);
  const { hideSnackbar } = useSnackbar();

  if (!snackbars || snackbars.length === 0) return null;

  // If your store pushes new items at the end, the newest is the last element.
  // If your store pushes new items at the front, adjust accordingly.
  // Here we treat the newest as the last element.
  const newest = snackbars[snackbars.length - 1];

  if (!newest) return null;

  return createPortal(
    <div
      aria-live="assertive"
      className="fixed top-4 right-4 z-50 flex flex-col gap-3 items-end max-w-full pointer-events-none"
      style={{ maxWidth: "min(420px, 95vw)" }}
    >
      <div style={{ pointerEvents: "auto" }}>
        <SnackbarItem
          snackbar={newest}
          onClose={hideSnackbar}
          duration={DEFAULT_DURATION_MS}
        />
      </div>
    </div>,
    document.body
  );
};

export default SnackbarContainer;
